import type {
  FetchAllTicketSaleTargetListParams,
  FetchTicketSaleListParams,
  FetchTicketSaleTargetListParams,
  TicketSale,
  TicketSaleTarget
} from 'Services/SalesManagement/Features/TicketSale/types'
import type { ListResponse } from 'Services/SalesManagement/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum TagTypes {
  TICKET_SALE = 'TicketSale',
  TICKET_SALE_TARGET = 'TicketSaleTarget'
}

export const ticketSaleApi = createBaseApi({
  reducerPath: 'ticketSaleApi',
  keepUnusedDataFor: 10,
  tagTypes: [TagTypes.TICKET_SALE, TagTypes.TICKET_SALE_TARGET]
}).injectEndpoints({
  endpoints: builder => ({
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getTicketSales: builder.query<ListResponse<TicketSale>, FetchTicketSaleListParams | void>({
      query: params => ({
        url: '/api/v1/ticket-sales/group-tickets/',
        params: params ?? undefined
      }),
      providesTags: result =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: TagTypes.TICKET_SALE, id })),
              { type: TagTypes.TICKET_SALE, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.TICKET_SALE, id: RTKCacheTags.LIST }]
    }),
    getSingleTicketSale: builder.query<TicketSale, string>({
      query: id => `/api/v1/ticket-sales/group-tickets/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.TICKET_SALE, id }]
    }),
    createSingleTicketSale: builder.mutation<TicketSale, Partial<TicketSale>>({
      query: data => ({
        url: `/api/v1/ticket-sales/group-tickets/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [{ type: TagTypes.TICKET_SALE, id: RTKCacheTags.LIST }]
    }),
    sendTicketSaleEmailReceipt: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/ticket-sales/group-tickets/${id}/receipt/`,
        method: 'POST'
      })
    }),
    getTicketSaleTargets: builder.query<
      ListResponse<TicketSaleTarget>,
      FetchTicketSaleTargetListParams | undefined
    >({
      query: params => ({
        url: '/api/v1/ticket-sales/targets/',
        params
      }),
      providesTags: result =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: TagTypes.TICKET_SALE_TARGET, id })),
              { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST }]
    }),
    getAllTicketSaleTargets: builder.query<
      TicketSaleTarget[],
      FetchAllTicketSaleTargetListParams | undefined
    >({
      query: params => ({
        url: '/api/v1/ticket-sales/targets/',
        params
      }),
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TagTypes.TICKET_SALE_TARGET, id })),
              { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST_ALL }
            ]
          : [{ type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST_ALL }]
    }),
    getSingleTicketSaleTarget: builder.query<TicketSaleTarget, string>({
      query: id => `/api/v1/ticket-sales/targets/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.TICKET_SALE_TARGET, id }]
    }),
    createSingleTicketSaleTarget: builder.mutation<TicketSaleTarget, Partial<TicketSaleTarget>>({
      query: data => ({
        url: `/api/v1/ticket-sales/targets/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST },
        { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    updateSingleTicketSaleTarget: builder.mutation<TicketSaleTarget, Partial<TicketSaleTarget>>({
      query: data => ({
        url: `/api/v1/ticket-sales/targets/${data.id}/`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TagTypes.TICKET_SALE_TARGET, id },
        { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST },
        { type: TagTypes.TICKET_SALE_TARGET, id: RTKCacheTags.LIST_ALL }
      ]
    })
  })
})

export const {
  useGetTicketSalesQuery,
  useGetSingleTicketSaleQuery,
  useCreateSingleTicketSaleMutation,
  useSendTicketSaleEmailReceiptMutation,
  useGetTicketSaleTargetsQuery,
  useGetAllTicketSaleTargetsQuery,
  useGetSingleTicketSaleTargetQuery,
  useCreateSingleTicketSaleTargetMutation,
  useUpdateSingleTicketSaleTargetMutation
} = ticketSaleApi
